// A book's World Builder view can include the series it belongs to (#1138).

export interface SeriesWithBooks {
  entityId: string;
  title: string;
  books?: { bookId: string; sequence?: number }[];
}

const bare = (id: string) => id.replace(/^BOOK#/, '');

/** Every series whose book list holds this book. Series store book ids prefixed or bare. */
export function seriesForBook<S extends SeriesWithBooks>(bookId: string, series: S[]): S[] {
  if (!bookId) return [];
  const id = bare(bookId);
  return series.filter(s => (s.books ?? []).some(b => b && typeof b.bookId === 'string' && bare(b.bookId) === id));
}

/** The switch's hover text, naming the series. */
export function seriesHint(titles: string[]): string {
  const names = titles.length <= 1 ? titles.join('')
    : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
  return `Show characters, locations and events from ${names} too`;
}

/** Remembered per browser; on unless the author turned it off. */
export const INCLUDE_SERIES_KEY = 'rf-include-series';

export function readIncludeSeries(): boolean {
  try { return localStorage.getItem(INCLUDE_SERIES_KEY) !== '0'; } catch { return true; }
}

export function writeIncludeSeries(on: boolean): void {
  try { localStorage.setItem(INCLUDE_SERIES_KEY, on ? '1' : '0'); } catch { /* storage blocked: the choice lasts this visit */ }
}

/**
 * What the book view asks the server for. Series are sent only for a book filter, when the
 * switch is on and the book is in at least one series. The switch shows whenever the book
 * is in a series, on or off.
 */
export function entitiesRequest<S extends SeriesWithBooks>(
  scope: string | null,
  id: string | null,
  includeSeries: boolean,
  seriesList: S[],
): { bookSeries: S[]; seriesIds: string[]; showSwitch: boolean } {
  const bookSeries = seriesForBook(scope === 'book' ? id ?? '' : '', seriesList);
  return {
    bookSeries,
    seriesIds: includeSeries ? bookSeries.map(s => s.entityId) : [],
    showSwitch: bookSeries.length > 0,
  };
}

/** What identifies a load: it changes only when the scope, the id or the series sent change. */
export function filterKey(scope: string | null, id: string | null, seriesIds: string[]): string {
  return [scope ?? '', id ?? '', seriesIds.join(',')].join('|');
}
